"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getQuote = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const utils_1 = require("@shapeshiftoss/utils");
const monads_1 = require("@sniptt/monads");
const qs_1 = __importDefault(require("qs"));
const types_1 = require("../../../../types");
const utils_2 = require("../../../../utils");
const constants_1 = require("../constants");
const getThresholdedAffiliateBps_1 = require("../getThresholdedAffiliateBps/getThresholdedAffiliateBps");
const poolAssetHelpers_1 = require("../poolAssetHelpers/poolAssetHelpers");
const thorService_1 = require("../thorService");
const _getQuote = async ({ sellAsset, buyAssetId, sellAmountCryptoBaseUnit, receiveAddress, streaming, affiliateBps, streamingInterval, }, deps) => {
    const buyPoolId = (0, poolAssetHelpers_1.assetIdToPoolAssetId)({ assetId: buyAssetId });
    const sellPoolId = (0, poolAssetHelpers_1.assetIdToPoolAssetId)({ assetId: sellAsset.assetId });
    const sellAmountCryptoPrecision = (0, utils_1.fromBaseUnit)(sellAmountCryptoBaseUnit, sellAsset.precision);
    // All THORChain pool amounts are base 8 regardless of token precision
    const sellAmountCryptoThorBaseUnit = (0, utils_1.bn)((0, utils_1.toBaseUnit)(sellAmountCryptoPrecision, constants_1.THORCHAIN_FIXED_PRECISION));
    // The THORChain swap endpoint expects BCH receiveAddress's to be stripped of the "bitcoincash:" prefix
    const parsedReceiveAddress = receiveAddress && buyAssetId === caip_1.bchAssetId
        ? receiveAddress.replace('bitcoincash:', '')
        : receiveAddress;
    const daemonUrl = deps.config.REACT_APP_THORCHAIN_NODE_URL;
    const queryString = qs_1.default.stringify({
        amount: sellAmountCryptoThorBaseUnit.toString(),
        from_asset: sellPoolId,
        to_asset: buyPoolId,
        destination: parsedReceiveAddress,
        affiliate_bps: affiliateBps,
        affiliate: constants_1.THORCHAIN_AFFILIATE_NAME,
        ...(streaming && { streaming_interval: streamingInterval }),
    });
    const maybeData = (await thorService_1.thorService.get(`${daemonUrl}/lcd/thorchain/quote/swap?${queryString}`)).andThen(({ data }) => (0, monads_1.Ok)(data));
    if (maybeData.isErr())
        return (0, monads_1.Err)(maybeData.unwrapErr());
    const data = maybeData.unwrap();
    const isError = 'error' in data;
    if (isError &&
        (/not enough fee/i.test(data.error) || /not enough to pay transaction fee/i.test(data.error))) {
        return (0, monads_1.Err)((0, utils_2.createTradeAmountTooSmallErr)());
    }
    else if (isError && /trading is halted/.test(data.error)) {
        return (0, monads_1.Err)((0, utils_2.makeSwapErrorRight)({
            message: `[getTradeRate]: Trading is halted, cannot process swap`,
            code: types_1.TradeQuoteError.TradingHalted,
            details: { sellAssetId: sellAsset.assetId, buyAssetId },
        }));
    }
    else if (isError) {
        return (0, monads_1.Err)((0, utils_2.makeSwapErrorRight)({
            message: data.error,
            code: types_1.TradeQuoteError.UnknownError,
        }));
    }
    else {
        return (0, monads_1.Ok)(data);
    }
};
const getQuote = async (input, deps) => {
    const { sellAsset, sellAmountCryptoBaseUnit, affiliateBps } = input;
    // don't apply an affiliate fee if it's below the outbound fee for the inbound pool
    const thresholdedAffiliateBps = await (0, getThresholdedAffiliateBps_1.getThresholdedAffiliateBps)({
        sellAsset,
        sellAmountCryptoBaseUnit,
        affiliateBps,
        config: deps.config,
    });
    return _getQuote({ ...input, affiliateBps: thresholdedAffiliateBps }, deps);
};
exports.getQuote = getQuote;
